export type PlatformIdentityKey =
  | 'instagram'
  | 'facebook'
  | 'meta-ads'
  | 'google-ads'
  | 'linkedin-ads'
  | 'linkedin'
  | 'tiktok'
  | 'pinterest'
  | 'youtube'
  | 'shopify'
  | 'google-analytics'
  | 'google-business'
  | 'google-drive'
  | 'x'
  | 'threads'
  | 'reddit'
  | 'slack';

interface PlatformIdentity {
  label: string;
  brandColorClass: string;
  dotColorClass: string;
  hexColor: string;
  order: number;
}

const PLATFORM_ALIASES: Record<string, PlatformIdentityKey> = {
  google_ads: 'google-ads',
  'google ads': 'google-ads',
  linkedin_ads: 'linkedin-ads',
  'linkedin ads': 'linkedin-ads',
  google_analytics: 'google-analytics',
  'google analytics': 'google-analytics',
  google_business: 'google-business',
  'google business': 'google-business',
  'google business profile': 'google-business',
  google_drive: 'google-drive',
  'google drive': 'google-drive',
  twitter: 'x',
  // `ad_units.ad_platform` / `ad_copy_platforms.ad_platform` (the paid-ad-copy
  // registry, distinct from the organic `posts.platforms` values above) store
  // 'meta' and 'google_rsa'. Both are ad-buying surfaces, not the organic
  // Facebook page or a generic Google product, so they alias to the Ads marks
  // — never to 'facebook' or a bare Google glyph — the same distinction
  // `PLATFORM_IDENTITY['meta-ads']`/`['google-ads']` already draw for
  // Settings → Connections.
  meta: 'meta-ads',
  google_rsa: 'google-ads',
};

export const PLATFORM_IDENTITY: Record<PlatformIdentityKey, PlatformIdentity> = {
  instagram: {
    label: 'Instagram',
    brandColorClass: 'text-platform-instagram',
    dotColorClass: 'bg-platform-instagram',
    hexColor: '#E4405F',
    order: 10,
  },
  facebook: {
    label: 'Facebook',
    brandColorClass: 'text-platform-facebook',
    dotColorClass: 'bg-platform-facebook',
    hexColor: '#1877F2',
    order: 20,
  },
  'meta-ads': {
    label: 'Meta Ads',
    brandColorClass: 'text-platform-meta-ads',
    dotColorClass: 'bg-platform-meta-ads',
    hexColor: '#0081FB',
    order: 25,
  },
  'google-ads': {
    label: 'Google Ads',
    // The supplied official mark carries its own colour; these values serve
    // text-only fallbacks, dots, and chart legends.
    brandColorClass: 'text-foreground',
    dotColorClass: 'bg-foreground',
    hexColor: '#4285F4',
    order: 26,
  },
  'linkedin-ads': {
    label: 'LinkedIn Ads',
    brandColorClass: 'text-platform-linkedin',
    dotColorClass: 'bg-platform-linkedin',
    hexColor: '#0A66C2',
    order: 27,
  },
  tiktok: {
    label: 'TikTok',
    brandColorClass: 'text-platform-tiktok',
    dotColorClass: 'bg-platform-tiktok',
    hexColor: '#FF0050',
    order: 30,
  },
  youtube: {
    label: 'YouTube',
    brandColorClass: 'text-platform-youtube',
    dotColorClass: 'bg-platform-youtube',
    hexColor: '#FF0000',
    order: 40,
  },
  linkedin: {
    label: 'LinkedIn',
    brandColorClass: 'text-platform-linkedin',
    dotColorClass: 'bg-platform-linkedin',
    hexColor: '#0A66C2',
    order: 50,
  },
  pinterest: {
    label: 'Pinterest',
    brandColorClass: 'text-platform-pinterest',
    dotColorClass: 'bg-platform-pinterest',
    hexColor: '#E60023',
    order: 60,
  },
  shopify: {
    label: 'Shopify',
    brandColorClass: 'text-platform-shopify',
    dotColorClass: 'bg-platform-shopify',
    hexColor: '#95BF47',
    order: 70,
  },
  'google-analytics': {
    label: 'Google Analytics',
    brandColorClass: 'text-platform-google-analytics',
    dotColorClass: 'bg-platform-google-analytics',
    hexColor: '#E37400',
    order: 80,
  },
  'google-business': {
    label: 'Google Business Profile',
    brandColorClass: 'text-platform-google-analytics',
    dotColorClass: 'bg-platform-google-analytics',
    hexColor: '#E37400',
    order: 90,
  },
  'google-drive': {
    label: 'Google Drive',
    brandColorClass: 'text-platform-google-drive',
    dotColorClass: 'bg-platform-google-drive',
    hexColor: '#4285F4',
    order: 100,
  },
  x: {
    label: 'X',
    brandColorClass: 'text-foreground',
    dotColorClass: 'bg-foreground',
    hexColor: '#888888',
    order: 110,
  },
  threads: {
    label: 'Threads',
    brandColorClass: 'text-foreground',
    dotColorClass: 'bg-foreground',
    hexColor: '#888888',
    order: 120,
  },
  reddit: {
    label: 'Reddit',
    brandColorClass: 'text-platform-reddit',
    dotColorClass: 'bg-platform-reddit',
    hexColor: '#FF4500',
    order: 130,
  },
  slack: {
    label: 'Slack',
    brandColorClass: 'text-platform-slack',
    dotColorClass: 'bg-platform-slack',
    hexColor: '#4A154B',
    order: 140,
  },
};

export function normalizePlatformKey(platform: unknown): string {
  return typeof platform === 'string' ? platform.toLowerCase().trim() : '';
}

export function getCanonicalPlatformKey(platform: unknown): string {
  const key = normalizePlatformKey(platform);
  return PLATFORM_ALIASES[key] ?? key;
}

export function getPlatformIdentity(platform: unknown): PlatformIdentity | null {
  const key = getCanonicalPlatformKey(platform);
  return PLATFORM_IDENTITY[key as PlatformIdentityKey] ?? null;
}

export function getPlatformLabel(platform: unknown): string {
  return getPlatformIdentity(platform)?.label ?? String(platform ?? '');
}

export function getPlatformBrandColorClass(platform: unknown): string {
  return getPlatformIdentity(platform)?.brandColorClass ?? 'text-muted-foreground';
}

export function getPlatformDotColorClass(platform: unknown): string {
  return getPlatformIdentity(platform)?.dotColorClass ?? 'bg-muted-foreground';
}

export function getPlatformHexColorValue(platform: unknown): string {
  return getPlatformIdentity(platform)?.hexColor ?? '#888888';
}

export function sortPlatformKeys(platforms: string[]): string[] {
  return [...platforms].sort((a, b) => {
    const priorityA = getPlatformIdentity(a)?.order ?? 999;
    const priorityB = getPlatformIdentity(b)?.order ?? 999;
    return priorityA - priorityB;
  });
}
